import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { C } from "./AccountingLayout";
import { toast } from "sonner";
import { Calendar, MapPin, Receipt, TrendingUp, ArrowUpRight, ArrowDownRight, Wallet, Package } from "lucide-react";
import DrillPanel from "./DrillPanel";

const money = (n) => (n == null ? "—" : `₦${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

export default function DailyBranchAccounting() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drillMetric, setDrillMetric] = useState(null);
  const locationName = (locations.find((l) => l.id === locationId) || {}).name;
  const openDrill = (metric) => setDrillMetric(metric);

  useEffect(() => { api.get("/locations").then((ls) => { setLocations(ls); if (!locationId && ls.length) setLocationId(ls[0].id); }).catch(() => {}); }, []);
  const load = () => {
    if (!date || !locationId) return;
    setLoading(true);
    api.get("/accounting/daily-branch", { params: { date, location_id: locationId } })
      .then(setData).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, locationId]);

  return (
    <div className="space-y-5" data-testid="daily-branch">
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-2xl" style={{ background: C.panel, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2"><Calendar size={16} style={{ color: C.burgundy }} /><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-date" className="h-9 px-3 rounded-lg text-sm" style={{ border: `1px solid ${C.border}` }} /></div>
        <div className="flex items-center gap-2"><MapPin size={16} style={{ color: C.burgundy }} /><select value={locationId} onChange={(e) => setLocationId(e.target.value)} data-testid="daily-loc" className="h-9 px-3 rounded-lg text-sm" style={{ border: `1px solid ${C.border}` }}>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
        <div className="text-xs" style={{ color: C.softMuted }}>{loading ? "Loading…" : "Live figures pulled from orders · journals · shifts · procurement · staff credits."}</div>
      </div>

      {data && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Gross Sales" value={money(data.sales.gross)} icon={Receipt} testid="kpi-gross" onClick={() => openDrill("gross_sales")} />
          <Kpi label="Net Sales" value={money(data.sales.net_sales)} icon={TrendingUp} testid="kpi-net" onClick={() => openDrill("net_sales")} />
          <Kpi label="Gross Profit" value={money(data.sales.gross_profit)} icon={ArrowUpRight} good testid="kpi-gp" onClick={() => openDrill("gross_profit")} />
          <Kpi label="Net Operating" value={money(data.net_operating)} icon={TrendingUp} testid="kpi-nop" onClick={() => openDrill("net_operating")} />
          <Kpi label="COGS" value={money(data.sales.cogs)} icon={Package} testid="kpi-cogs" onClick={() => openDrill("cogs")} />
          <Kpi label="Expenses" value={money(data.expenses.total)} icon={ArrowDownRight} bad testid="kpi-exp" onClick={() => openDrill("expenses")} />
          <Kpi label="VAT" value={money(data.sales.vat)} icon={Receipt} testid="kpi-vat" onClick={() => openDrill("vat")} />
          <Kpi label="Refunds" value={money(data.sales.refunds)} icon={ArrowDownRight} bad testid="kpi-refunds" onClick={() => openDrill("refunds")} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Tenders">
            {["cash", "card", "transfer", "loyalty", "staff_credit", "other"].map((k) => (
              <Row key={k} label={k.replace("_", " ")} value={money(data.tenders[k] || 0)} onClick={() => openDrill(`tender_${k}`)} testid={`tender-${k}`} />
            ))}
          </Card>
          <Card title="TILL Shifts">
            {data.tills.length === 0 && <div className="text-xs" style={{ color: C.softMuted }}>No shifts opened today.</div>}
            {data.tills.map((s) => (
              <button key={s.shift_id} onClick={() => openDrill("till_variance")} className="w-full text-left text-xs py-2 border-b last:border-0 hover:bg-[#F5EDDC]" style={{ borderColor: C.borderSoft, color: C.text }}>
                <div className="font-semibold">{s.till} · {s.status}</div>
                <div style={{ color: C.softMuted }}>float {money(s.opening_float)} · counted cash {money(s.counted_cash)} · variance {money(s.cash_variance)}</div>
              </button>
            ))}
          </Card>
          <Card title="Cash Movements" onClick={() => openDrill("cash_movements")}>
            {data.cash_movements.length === 0 && <div className="text-xs" style={{ color: C.softMuted }}>No cash movements approved today.</div>}
            {data.cash_movements.map((c) => (
              <Row key={c.id} label={`${c.direction || "?"} · ${c.till || ""}`} value={money(c.amount)} />
            ))}
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title={`Procurement (${data.procurement.length})`} onClick={() => openDrill("procurement")}>
            {data.procurement.length === 0 && <div className="text-xs" style={{ color: C.softMuted }}>No goods received today.</div>}
            {data.procurement.map((g) => <Row key={g.id} label={`${g.number} · ${g.supplier_name}`} value={money(g.total_cost)} />)}
          </Card>
          <Card title={`Staff Credit (${data.staff_credit.length})`} onClick={() => openDrill("staff_credit")}>
            {data.staff_credit.length === 0 && <div className="text-xs" style={{ color: C.softMuted }}>No staff credit activity today.</div>}
            {data.staff_credit.map((c) => <Row key={c.id} label={`${c.employee_name} · ${c.type}`} value={money(c.amount)} />)}
          </Card>
        </div>

        <Card title={`Orders (${data.orders_ref.length}) — drill to journal`} onClick={() => openDrill("orders")}>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr style={{ color: C.softMuted }}><th className="text-left py-2 pr-3 text-[10px] tracking-widest">ORDER</th><th className="text-left py-2 pr-3 text-[10px] tracking-widest">TIME</th><th className="text-left py-2 pr-3 text-[10px] tracking-widest">TILL</th><th className="text-right py-2 pr-3 text-[10px] tracking-widest">TOTAL</th></tr></thead>
            <tbody>
              {data.orders_ref.slice(0, 40).map((o) => (
                <tr key={o.id} className="border-t" style={{ borderColor: C.borderSoft, color: C.text }} data-testid={`order-row-${o.id}`}>
                  <td className="py-2 pr-3 font-semibold" style={{ color: C.burgundy }}>{o.number}</td>
                  <td className="py-2 pr-3">{(o.created_at || "").slice(11, 19)}</td>
                  <td className="py-2 pr-3">{o.till || "—"}</td>
                  <td className="py-2 pr-3 text-right font-semibold">{money(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>

        <Card title={`Journals posted (${data.journals.length})`} onClick={() => openDrill("journals")}>
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr style={{ color: C.softMuted }}><th className="text-left py-2 pr-3 text-[10px] tracking-widest">JOURNAL</th><th className="text-left py-2 pr-3 text-[10px] tracking-widest">SOURCE</th><th className="text-left py-2 pr-3 text-[10px] tracking-widest">MEMO</th></tr></thead>
            <tbody>
              {data.journals.slice(0, 30).map((j) => (
                <tr key={j.id} className="border-t" style={{ borderColor: C.borderSoft, color: C.text }}>
                  <td className="py-2 pr-3 font-semibold" style={{ color: C.burgundy }}>{j.number}</td>
                  <td className="py-2 pr-3">{j.source}·{j.source_event}</td>
                  <td className="py-2 pr-3">{j.memo}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </Card>
      </>}

      <DrillPanel open={!!drillMetric} onClose={() => setDrillMetric(null)} metric={drillMetric} date={date} locationId={locationId} locationName={locationName} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, good, bad, testid, onClick }) {
  return (
    <button onClick={onClick} className="rounded-2xl p-4 border text-left hover:shadow-md hover:-translate-y-0.5 transition" style={{ background: C.panel, borderColor: C.border }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2"><div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>{label}</div><Icon size={16} style={{ color: good ? C.green : bad ? C.red : C.gold }} /></div>
      <div className="font-brand font-bold text-xl" style={{ color: C.text }}>{value}</div>
      <div className="text-[10px] mt-1" style={{ color: C.softMuted }}>Click to drill</div>
    </button>
  );
}
function Card({ title, children, onClick }) {
  return (
    <div className="rounded-2xl border p-4" style={{ background: C.panel, borderColor: C.border }}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-widest uppercase font-semibold" style={{ color: C.softMuted }}>{title}</div>
        {onClick && <button onClick={onClick} className="text-[10px] tracking-widest uppercase font-semibold hover:underline" style={{ color: C.burgundy }}>Drill →</button>}
      </div>
      {children}
    </div>
  );
}
function Row({ label, value, onClick, testid }) {
  const inner = (
    <>
      <span className="capitalize" style={{ color: C.muted }}>{label}</span>
      <b>{value}</b>
    </>
  );
  if (onClick) return <button onClick={onClick} className="w-full flex items-center justify-between text-sm py-1.5 border-b last:border-0 hover:bg-[#F5EDDC] px-1 -mx-1 rounded" style={{ borderColor: C.borderSoft, color: C.text }} data-testid={testid}>{inner}</button>;
  return <div className="flex items-center justify-between text-sm py-1.5 border-b last:border-0" style={{ borderColor: C.borderSoft, color: C.text }} data-testid={testid}>{inner}</div>;
}
